import ReactGA from 'react-ga4'
import MatomoTracker from '@jonkoops/matomo-tracker'

class GoogleAnalyticsNull {
  event() {}
}

class MatomoNull {
  trackEvent() {}
  trackPageView() {}
  trackEcommerceOrder() {}
  addEcommerceItem() {}
}

class Tracking {
  constructor ({ enabled, gaId, mtId, mtUrl, debug }) {
    this.isBrowser = typeof window !== "undefined"

    this.enabled = enabled && this.isBrowser
    this.gaId = gaId
    this.mtId = mtId
    this.mtUrl = mtUrl
    this.debug = debug
    this.gaInitialized = false
    this.setTracker()
  }

  setTracker() {
    // for tracking to work you have to set "enabled" on initialization.
    this.consented = this.getConsented()

    if (this.enabled) {
      if (this.gaId && this.consented) {
        if (!this.gaInitialized) {
          ReactGA.initialize([{
            trackingId: this.gaId,
            debug: this.debug
          }])
          ReactGA.ga("set", "anonymizeIp", true)
          this.gaInitialized = true
        }
        this.google = ReactGA
      } else {
        this.google = new GoogleAnalyticsNull()
      }

      if (this.mtId && this.mtUrl) {
        this.matomo = new MatomoTracker({
          urlBase: this.mtUrl,
          siteId: this.mtId,
          configurations: {
            // In order for us to be GDPR compatible
            // we need to work cookieless with matomo
            requireCookieConsent: true
          },
        })
      } else {
        this.matomo = new MatomoNull()
      }
    } else {
      this.google = new GoogleAnalyticsNull()
      this.matomo = new MatomoNull()
    }
  }

  log = (...args) => this.debug && console.debug(...args)

  trackEvent = (category, action) => {
    this.log(`firing 'trackEvent' with action '${action}'`)
    this.setTracker()
    this.google.event({
      category,
      action
    })
    this.matomo.trackEvent({
      category,
      action
    })
  }

  trackDetailedEvent = ({ category, action, name, value }) => {
    this.setTracker()
    this.matomo.trackEvent({
      category,
      action,
      name,
      value
    })
  }

  trackPageView = () => {
    this.log("firing 'trackPageview'")
    this.setTracker()

    this.matomo.trackPageView()
  }

  trackException = (message) => {
    this.log("firing 'trackException'")
    this.setTracker()

    this.google.event('exception', {
      description: message,
      fatal: true
    })
    this.matomo.trackEvent({
      category: "Exception",
      action: message
    })
  }

  trackEcommerce = (transactionId, items) => {
    this.log("firing 'trackEcommerce'")
    this.setTracker()

    const transactionItems = items.map(({ id, name, category }) => {
      return {
        item_id: id,
        item_name: name,
        item_category: category
      }
    })
    this.google.event('purchase', {
      transaction_id: transactionId,
      items: transactionItems
    })

    this.matomo.trackEcommerceOrder({
      orderId: transactionId,
      orderRevenue: 0
    })

    items.forEach(({ name, category, sku }) => {
      this.matomo.addEcommerceItem({ sku, productName: name, productCategory: category })
    })
  }

  getConsented() {
    if (!this.isBrowser) {
      return false
    }

    let consented = false
    const googleTagData = (window != null) && window["google_tag_data"]
    // https://www.cookieinfo.net/en/knowledge-base/check-if-google-consent-mode-v2-is-implemented/
    const consentData = googleTagData && googleTagData.ics && googleTagData.ics.entries
    if (consentData) {
      const analyticsStorage = consentData.analytics_storage
      if ((analyticsStorage.update === true)
        || (analyticsStorage.default === true && analyticsStorage.update == undefined)) {
        consented = true
      }
    } else {
      console.warn("No Consent Mode data found");
    }

    return consented
  }
}

export default Tracking
